import React, { useState, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { clientService, Client } from "../services/client.service";
import apiClient from "../services/api";

export interface PurchaseOrderHeaderData {
  poNumber: string;
  clientID: number;
  clientName: string;
  subClientId?: number;
  subClientName?: string;
  poDate: string;
  comment: string;
  statusID?: number;
  statusName?: string;
  priorityID?: number;
  priorityName?: string;
  signatureRequired?: boolean;
  fromInventory?: boolean;
}

interface Props {
  data: PurchaseOrderHeaderData;
  onChange: (data: PurchaseOrderHeaderData) => void;
  disabled?: boolean;
  showNextPoNumber?: boolean;
}

const PurchaseOrderHeaderFields: React.FC<Props> = ({
  data,
  onChange,
  disabled = false,
  showNextPoNumber = false,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [nextPoNumber, setNextPoNumber] = useState("");

  useEffect(() => {
    loadClients();
    if (showNextPoNumber) {
      loadNextPoNumber();
    }
  }, [showNextPoNumber]);

  const loadClients = async () => {
    try {
      const allClients = await clientService.getClients();
      // Filter to only active clients (statusID = 1)
      const activeClients = allClients.filter((c) => c.statusID === 1);
      setClients(activeClients);
    } catch (err) {
      console.error("Failed to load clients:", err);
    }
  };

  const loadNextPoNumber = async () => {
    try {
      const response = await apiClient.get("/purchase-orders/next-po-number");
      setNextPoNumber(response.data.nextPoNumber);
    } catch (err) {
      console.error("Failed to load next PO number:", err);
    }
  };

  const handleClientChange = (clientID: number) => {
    const client = clients.find((c) => c.id === clientID);
    onChange({
      ...data,
      clientID,
      clientName: client?.companyName || "",
    });
  };

  const handleSubClientChange = (subClientId: number) => {
    const subClient = clients.find((c) => c.id === subClientId);
    onChange({
      ...data,
      subClientId: subClientId || undefined,
      subClientName: subClient?.companyName || "",
    });
  };

  return (
    <Box>
      <TextField
        fullWidth
        required
        label='PO Number'
        value={data.poNumber}
        onChange={(e) => onChange({ ...data, poNumber: e.target.value })}
        disabled={disabled}
        helperText={nextPoNumber ? `Suggested: ${nextPoNumber}` : ""}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth required sx={{ mb: 2 }}>
        <InputLabel>Client</InputLabel>
        <Select
          value={data.clientID || ""}
          label='Client'
          onChange={(e) => handleClientChange(Number(e.target.value))}
          disabled={disabled}
        >
          <MenuItem value=''>
            <em>Select a client</em>
          </MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.id} value={client.id}>
              {client.companyName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Sub Client (Optional)</InputLabel>
        <Select
          value={data.subClientId || ""}
          label='Sub Client (Optional)'
          onChange={(e) => handleSubClientChange(Number(e.target.value))}
          disabled={disabled}
        >
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.id} value={client.id}>
              {client.companyName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        type='date'
        label='PO Date'
        InputLabelProps={{ shrink: true }}
        value={data.poDate}
        onChange={(e) => onChange({ ...data, poDate: e.target.value })}
        disabled={disabled}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        multiline
        rows={3}
        label='Comments'
        value={data.comment}
        onChange={(e) => onChange({ ...data, comment: e.target.value })}
        disabled={disabled}
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={data.statusID || ""}
              label='Status'
              onChange={(e) =>
                onChange({ ...data, statusID: Number(e.target.value) })
              }
              disabled={disabled}
            >
              <MenuItem value={1}>Pending</MenuItem>
              <MenuItem value={2}>Complete</MenuItem>
              <MenuItem value={3}>On Hold</MenuItem>
              <MenuItem value={4}>Partial</MenuItem>
              <MenuItem value={5}>Building</MenuItem>
              <MenuItem value={6}>Request: Pending</MenuItem>
              <MenuItem value={7}>Request: Complete</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={data.priorityID || ""}
              label='Priority'
              onChange={(e) =>
                onChange({ ...data, priorityID: Number(e.target.value) })
              }
              disabled={disabled}
            >
              <MenuItem value={1}>Low</MenuItem>
              <MenuItem value={2}>Normal</MenuItem>
              <MenuItem value={3}>High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.fromInventory || false}
                onChange={(e) =>
                  onChange({ ...data, fromInventory: e.target.checked })
                }
                disabled={disabled}
              />
            }
            label='From Inventory'
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.signatureRequired || false}
                onChange={(e) =>
                  onChange({ ...data, signatureRequired: e.target.checked })
                }
                disabled={disabled}
              />
            }
            label='Signature Required'
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PurchaseOrderHeaderFields;
